import { useEffect, useRef } from 'react';
import { trackLandingSectionViewed } from '@/lib/analytics';

interface SectionTrackingOptions {
  /** Visibility fraction that counts as an impression. Default 0.35. */
  threshold?: number;
  /**
   * Fire on mount when IntersectionObserver is unavailable (jsdom, very old
   * browsers) instead of silently dropping the impression. Default false —
   * the original behavior for page sections.
   */
  fallbackOnUnsupported?: boolean;
}

export function useLandingSectionTracking<T extends HTMLElement = HTMLElement>(
  section: string,
  { threshold = 0.35, fallbackOnUnsupported = false }: SectionTrackingOptions = {}
) {
  const sectionRef = useRef<T | null>(null);

  useEffect(() => {
    const element = sectionRef.current;
    if (!element || typeof window === 'undefined') {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      if (fallbackOnUnsupported) {
        trackLandingSectionViewed(section);
      }
      return;
    }

    let hasTracked = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || hasTracked) {
          return;
        }

        hasTracked = true;
        trackLandingSectionViewed(section);
        observer.disconnect();
      },
      { threshold }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [section, threshold, fallbackOnUnsupported]);

  return sectionRef;
}
